import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@scripts/react/components/Button";
import { CardHeader } from "./CardHeader";
import { CardThumbnails, Thumbnail } from "./CardThumbnails";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { getLabelColor, updateLabelColor } from "@/components/annotation/colors";
import { BuildPromptSteps } from "@/types/flows";
import { useModel } from "@/contexts/ModelContext";
import { useFlow } from "@/contexts/FlowContext";
import { useFrame } from "@/contexts/FrameContext";
import type { Annotation } from "@/types/annotation";
import { Chip } from "@scripts/react/components/Chip";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent, AppSource } from "@/analytics/types";
import { useCurrentMedia, useMediaStore, useTextPromptMode, useSyncPromptLabels } from "@/store";
import { ConfidenceSlider } from "@/components/ConfidenceSlider";
import { useCoreApp } from "@/contexts/CoreAppContext";
import colors from "tailwindcss/colors";
import { useMediaUtils } from "@/hooks/useMediaUtils";
import {
    filterByFileId,
    annotationsToThumbnails,
    deleteAnnotationsByLabel,
    deleteAnnotationsByLabelCompletely,
    hasRealAnnotations,
    countRealAnnotations
} from "./cardUtils";
import { TooltipContainer } from "@scripts/react/components/Tooltip.jsx";
import { NoResultsEmptyState } from "../NoResultsEmptyState";

interface PromptLabelAnnotateCardProps {
    name: string;
    annotationsForThisLabel: (Annotation & { frameId: string; fileId: string })[];
    allAnnotationsForThisLabel: (Annotation & { frameId: string; fileId: string })[];
    allAnnotationsIncludingDrafts: (Annotation & { frameId: string; fileId: string })[];
    initialConfidenceThreshold?: number;
    inputRef: React.RefObject<HTMLInputElement | null>;
    initiallyEditing?: boolean;
}

export const PromptLabelAnnotateCard: React.FC<PromptLabelAnnotateCardProps> = ({
    name,
    inputRef,
    initialConfidenceThreshold,
    annotationsForThisLabel,
    allAnnotationsForThisLabel,
    allAnnotationsIncludingDrafts,
    initiallyEditing = false
}) => {
    const textPromptMode = useTextPromptMode();
    const { setAnnotationsForFileAndFrameId, textPrompts, updatePromptLabels, promptLabels } =
        useModel();
    const syncPromptLabels = useSyncPromptLabels();
    const {
        setPreviouslySelectedObject,
        setCurrentFrameAnnotations,
        flowLocked,
        previouslySelectedObject,
        selectedObject
    } = useFlow();
    const { setSelectedAnnotationId, setShouldShowAnnotationInput } = useFrame();
    const { track } = useAnalytics();
    const currentMedia = useCurrentMedia();
    const { setCurrentMedia } = useMediaUtils();
    const media = useMediaStore();
    const { showSliderHint } = useCoreApp();

    const [isHovered, setIsHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(initiallyEditing);
    const [editValue, setEditValue] = useState(name);
    const [pendingDelete, setPendingDelete] = useState<{
        annotationId: string;
        frameId: string;
        fileId: string;
    } | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // Annotations filtered by confidence threshold and current file
    const visibleAnnotationsForCurrentFile = useMemo(
        () => filterByFileId(annotationsForThisLabel, currentMedia?.file.id),
        [annotationsForThisLabel, currentMedia?.file.id]
    );

    // Confirmed annotations for current file (including those filtered out by confidence)
    // This excludes drafts (predictions that haven't been approved yet)
    const allAnnotationsForCurrentFile = useMemo(
        () => filterByFileId(allAnnotationsForThisLabel, currentMedia?.file.id),
        [allAnnotationsForThisLabel, currentMedia?.file.id]
    );

    // Get all annotations including drafts for current file
    const allAnnotationsIncludingDraftsForCurrentFile = useMemo(
        () => filterByFileId(allAnnotationsIncludingDrafts, currentMedia?.file.id),
        [allAnnotationsIncludingDrafts, currentMedia?.file.id]
    );

    // Show slider/thumbnails if there are confirmed annotations OR draft predictions for this file
    const hasAnnotationsOnCurrentFile = useMemo(() => {
        const hasConfirmedAnnotations = hasRealAnnotations(allAnnotationsForCurrentFile);
        return hasConfirmedAnnotations || allAnnotationsIncludingDraftsForCurrentFile.length > 0;
    }, [allAnnotationsForCurrentFile, allAnnotationsIncludingDraftsForCurrentFile]);

    // Check if there are annotations to clear (vs deleting the entire object)
    const hasAnnotationsToClear = useMemo(() => {
        const hasOnlyPlaceholderAnnotations =
            allAnnotationsIncludingDraftsForCurrentFile.length > 0 &&
            !hasRealAnnotations(allAnnotationsIncludingDraftsForCurrentFile);
        return hasAnnotationsOnCurrentFile && !hasOnlyPlaceholderAnnotations;
    }, [hasAnnotationsOnCurrentFile, allAnnotationsIncludingDraftsForCurrentFile]);

    // Count only annotations with real dimensions (exclude empty placeholders)
    const realAnnotationsCount = useMemo(
        () => countRealAnnotations(visibleAnnotationsForCurrentFile),
        [visibleAnnotationsForCurrentFile]
    );

    // Convert annotations to thumbnails for display
    const thumbnails = useMemo(
        () => annotationsToThumbnails(visibleAnnotationsForCurrentFile),
        [visibleAnnotationsForCurrentFile]
    );

    const handleThumbnailClick = useCallback(
        (e: React.MouseEvent, thumbnail: Thumbnail) => {
            e.stopPropagation(); // Prevent click from bubbling to parent div

            // Block all interactions during model generation
            if (flowLocked) {
                return;
            }

            if (!thumbnail || !thumbnail.frameId || !thumbnail.id || !thumbnail.fileId) {
                console.warn(
                    "Clicked annotation thumbnail is missing frameId, fileId, or id",
                    thumbnail
                );
                return;
            }

            const targetMediaItem = media[thumbnail.fileId];
            const targetFrame = targetMediaItem.isVideo
                ? media[thumbnail.frameId]
                : targetMediaItem;

            if (!targetMediaItem || !targetFrame) {
                console.warn("Target media item or frame not found", thumbnail);
                return;
            }

            setCurrentMedia({
                file: targetMediaItem,
                frame: targetFrame,
                frameSegment: targetFrame.metadata?.segment!
            });

            // 3. Set selected annotation ID in context and flag to show input
            setSelectedAnnotationId(thumbnail.id);
            setShouldShowAnnotationInput(true);

            // Video seeking will be handled by FilePreview reacting to frame changes
        },
        [flowLocked, media, setSelectedAnnotationId, setShouldShowAnnotationInput, setCurrentMedia]
    );

    const handleEditClick = useCallback(
        (e?: React.MouseEvent) => {
            e?.stopPropagation(); // Prevent triggering the card click

            // Block all interactions during model generation
            if (flowLocked) {
                return;
            }

            setIsEditing(true);
            setEditValue(name);

            // Focus input after state update
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select();
                }
            }, 0);
        },
        [flowLocked, inputRef, name]
    );

    const handleObjectCardClick = useCallback(() => {
        // Block all interactions during model generation
        if (flowLocked) {
            return;
        }

        // Set this object as the previously selected object for drawing new bounding boxes
        setPreviouslySelectedObject(name);

        // Find the first annotation of this object type and select it
        const firstAnnotationOfThisType = annotationsForThisLabel[0];
        if (
            firstAnnotationOfThisType &&
            firstAnnotationOfThisType.frameId &&
            firstAnnotationOfThisType.fileId &&
            !textPromptMode
        ) {
            const targetMediaItem = media[firstAnnotationOfThisType.fileId];
            const targetFrame = targetMediaItem.isVideo
                ? media[firstAnnotationOfThisType.frameId]
                : targetMediaItem;

            if (!targetMediaItem || !targetFrame) {
                console.warn("Target media item or frame not found", firstAnnotationOfThisType);
                return;
            }

            setCurrentMedia({
                file: targetMediaItem,
                frame: targetFrame,
                frameSegment: targetFrame.metadata?.segment!
            });

            setSelectedAnnotationId(firstAnnotationOfThisType.id);
        } else {
            // If no annotations exist for this object type, deselect
            setSelectedAnnotationId(null);
        }
    }, [
        annotationsForThisLabel,
        setPreviouslySelectedObject,
        setSelectedAnnotationId,
        flowLocked,
        setCurrentMedia,
        media,
        name,
        textPromptMode
    ]);

    // Function to rename all annotations with the old name to the new name
    const handleEditLabel = useCallback(
        (newName: string) => {
            if (newName === name) return;

            // Preserve the old label settings before renaming
            const oldLabelSettings = promptLabels.get(name);

            setAnnotationsForFileAndFrameId((prev) => {
                const updated = { ...prev };

                // Update all annotations with the old name across all files and frames
                Object.keys(updated).forEach((fileId) => {
                    Object.keys(updated[fileId]).forEach((frameId) => {
                        updated[fileId][frameId] = updated[fileId][frameId].map((annotation) => {
                            if (annotation.label === name) {
                                return {
                                    ...annotation,
                                    label: newName,
                                    color: getLabelColor(promptLabels, newName),
                                    empty: false
                                };
                            }
                            return annotation;
                        });
                    });
                });

                return updated;
            });

            const labelsToKeep = Array.from(promptLabels.keys())
                .filter((labelName) => labelName !== name)
                .concat(newName);

            if (oldLabelSettings) {
                updatePromptLabels(
                    [
                        {
                            name: newName,
                            confidenceThreshold: oldLabelSettings.confidenceThreshold,
                            customColor: oldLabelSettings.customColor
                        }
                    ],
                    [name] // Delete the old name
                );
            } else {
                // If no old settings, just delete the old name
                updatePromptLabels([], [name]);
            }

            // Update previouslySelectedObject so future annotations will use the new name
            setPreviouslySelectedObject(newName);

            // Prevent race conditions where syncPromptLabels might read stale state.
            setTimeout(() => {
                syncPromptLabels(labelsToKeep);
            }, 0);

            // Force a canvas redraw by updating the current frame annotations
            // This ensures the canvas immediately reflects the changes
            setTimeout(() => {
                setCurrentFrameAnnotations((prev: any) => {
                    return [...prev];
                });
            }, 50);
        },
        [
            setAnnotationsForFileAndFrameId,
            setPreviouslySelectedObject,
            setCurrentFrameAnnotations,
            name,
            promptLabels,
            updatePromptLabels,
            syncPromptLabels
        ]
    );

    const handleDeleteLabel = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation(); // Prevent triggering the card click

            // Block all interactions during model generation
            if (flowLocked) {
                return;
            }

            if (hasAnnotationsToClear) {
                // Step 1: Clear annotations on current file only
                setAnnotationsForFileAndFrameId((prev) => {
                    return deleteAnnotationsByLabel(prev, name, currentMedia?.file.id);
                });
            } else {
                // Step 2: Delete all annotations across all files and remove from prompt labels
                setAnnotationsForFileAndFrameId((prev) => {
                    return deleteAnnotationsByLabelCompletely(prev, name);
                });

                // Calculate remaining label names before update to avoid stale closure issues.
                const remainingLabelNames = Array.from(promptLabels.keys()).filter(
                    (labelName) => labelName !== name
                );

                updatePromptLabels([], [name]);

                // Ensures the deleted label is removed but other labels without annotations are preserved
                setTimeout(() => {
                    syncPromptLabels(remainingLabelNames);
                }, 0);
            }

            // Clear selection if this object was selected
            if (selectedObject === name || previouslySelectedObject === name) {
                setSelectedAnnotationId(null);
                setPreviouslySelectedObject(null);
            }

            // Force a canvas redraw
            setTimeout(() => {
                setCurrentFrameAnnotations((prev: any) => {
                    return [...prev];
                });
            }, 50);
        },
        [
            flowLocked,
            name,
            selectedObject,
            previouslySelectedObject,
            setSelectedAnnotationId,
            setPreviouslySelectedObject,
            setCurrentFrameAnnotations,
            setAnnotationsForFileAndFrameId,
            currentMedia?.file.id,
            promptLabels,
            updatePromptLabels,
            hasAnnotationsToClear,
            syncPromptLabels
        ]
    );

    const handleSaveEdit = useCallback(() => {
        const trimmedValue = editValue.trim().toLowerCase();
        if (trimmedValue && trimmedValue !== name) {
            handleEditLabel(trimmedValue);
        }
        setIsEditing(false);
    }, [editValue, name, handleEditLabel]);

    const handleEditBlur = useCallback(() => {
        handleSaveEdit();
    }, [handleSaveEdit]);

    const handleCancelEdit = useCallback(() => {
        setEditValue(name);
        setIsEditing(false);
    }, [name]);

    const handleEditKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            e.stopPropagation(); // Prevent event from bubbling up to parent div

            if (e.key === "Enter") {
                e.preventDefault();
                handleSaveEdit();
            } else if (e.key === "Escape") {
                handleCancelEdit();
            }
        },
        [handleSaveEdit, handleCancelEdit]
    );

    // Focus input when initially editing
    useEffect(() => {
        if (initiallyEditing) {
            handleEditClick();
        }
    }, [handleEditClick, initiallyEditing, inputRef]);

    const handleDeleteAnnotationClick = useCallback(
        (e: React.MouseEvent, thumbnail: Thumbnail) => {
            e.stopPropagation(); // Important: Stop event from bubbling to parent elements

            // Block all interactions during model generation
            if (flowLocked) {
                return;
            }

            if (!thumbnail || !thumbnail.frameId || !thumbnail.id || !thumbnail.fileId) {
                console.warn(
                    "Attempted to delete annotation missing frameId, fileId, or id",
                    thumbnail
                );
                return;
            }

            // Track annotation deletion from thumbnail
            track({
                event: AnalyticsFlowEvent.ANNOTATION_OBJECT_DELETED,
                data: {
                    appSource: AppSource.RAPID,
                    source: "thumbnail",
                    deletionMethod: "thumbnail_click",
                    annotationId: thumbnail.id,
                    annotationLabel: name,
                    frameId: thumbnail.frameId,
                    fileId: thumbnail.fileId,
                    remainingAnnotations: annotationsForThisLabel.length - 1
                }
            });

            setPendingDelete({
                annotationId: thumbnail.id,
                frameId: thumbnail.frameId as string,
                fileId: thumbnail.fileId
            });

            const targetMediaItem = media[thumbnail.fileId];
            const targetFrame = targetMediaItem.isVideo
                ? media[thumbnail.frameId]
                : targetMediaItem;

            if (!targetMediaItem || !targetFrame) {
                console.warn("Target media item or frame not found", thumbnail);
                return;
            }

            setCurrentMedia({
                file: targetMediaItem,
                frame: targetFrame,
                frameSegment: targetFrame.metadata?.segment!
            });

            setSelectedAnnotationId(null);
        },
        [
            flowLocked,
            setSelectedAnnotationId,
            track,
            name,
            annotationsForThisLabel.length,
            setCurrentMedia,
            media
        ]
    );

    // Effect to handle actual deletion once frame navigation is complete
    useEffect(() => {
        if (pendingDelete && currentMedia?.frame?.id === pendingDelete.frameId) {
            setCurrentFrameAnnotations((prevFrameAnnotations: any) => {
                if (!prevFrameAnnotations) return [];
                return prevFrameAnnotations.filter(
                    (anno: any) => anno.id !== pendingDelete.annotationId
                );
            });
            setPendingDelete(null); // Reset pending delete state
        }
    }, [pendingDelete, currentMedia?.frame?.id, setCurrentFrameAnnotations]);

    // Drag and drop handlers
    const handleThumbnailDragStart = useCallback(
        (e: React.DragEvent, thumbnail: Thumbnail) => {
            if (flowLocked) {
                e.preventDefault();
                return;
            }

            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData(
                "text/plain",
                JSON.stringify({
                    annotationId: thumbnail.id,
                    fileId: thumbnail.fileId,
                    frameId: thumbnail.frameId,
                    sourceCardName: name
                })
            );
        },
        [flowLocked, name]
    );

    const handleThumbnailDragEnd = useCallback(() => {
        // Clean up any drag state if needed
    }, []);

    const handleCardDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (!flowLocked) {
                setIsDragOver(true);
            }
        },
        [flowLocked]
    );

    const handleCardDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        // Only set drag over to false if we're actually leaving the card
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragOver(false);
        }
    }, []);

    const handleCardDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);

            if (flowLocked) {
                return;
            }

            try {
                const dragData = JSON.parse(e.dataTransfer.getData("text/plain"));
                const { annotationId, fileId, frameId, sourceCardName } = dragData;

                // Don't allow dropping on the same card
                if (sourceCardName === name) {
                    return;
                }

                // Set the previously selected object (similar to AnnotationLabelInput)
                setPreviouslySelectedObject(name);

                // Reassign the annotation to this card's label
                setAnnotationsForFileAndFrameId((prev) => {
                    const updated = { ...prev };

                    // Update the annotation with new label and modifiedAt timestamp (similar to AnnotationLabelInput)
                    if (updated[fileId] && updated[fileId][frameId]) {
                        updated[fileId][frameId] = updated[fileId][frameId].map((annotation) => {
                            if (annotation.id === annotationId) {
                                return {
                                    ...annotation,
                                    label: name,
                                    color: getLabelColor(promptLabels, name),
                                    modifiedAt: Date.now()
                                };
                            }
                            return annotation;
                        });
                    }

                    return updated;
                });

                // Force a canvas redraw
                setTimeout(() => {
                    setCurrentFrameAnnotations((prev) => {
                        return [...prev];
                    });
                }, 50);
            } catch (error) {
                console.error("Error handling drop:", error);
            }
        },
        [
            flowLocked,
            name,
            setAnnotationsForFileAndFrameId,
            setCurrentFrameAnnotations,
            setPreviouslySelectedObject,
            promptLabels
        ]
    );

    const labelColor = getLabelColor(promptLabels, name);

    return (
        <div
            className={twClassNames(
                "promptCard relative",
                flowLocked
                    ? "animate-pulse cursor-not-allowed"
                    : textPromptMode
                      ? "cursor-default"
                      : "cursor-pointer"
            )}
            onClick={handleObjectCardClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onDragOver={handleCardDragOver}
            onDragLeave={handleCardDragLeave}
            onDrop={handleCardDrop}
            style={
                {
                    "--tw-ring-color":
                        showSliderHint && hasAnnotationsOnCurrentFile
                            ? `${labelColor}40`
                            : "transparent",
                    ...(!textPromptMode &&
                    !flowLocked &&
                    (selectedObject === name || previouslySelectedObject === name)
                        ? {
                              borderColor: labelColor,
                              backgroundColor: `${labelColor}08` // 3% opacity
                          }
                        : !textPromptMode && isHovered && !flowLocked
                          ? { borderColor: labelColor } // Object color on hover
                          : { borderColor: colors.gray[300] })
                } as React.CSSProperties
            }
        >
            {/* Drop indicator */}
            {isDragOver && !flowLocked && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-gray-900 bg-opacity-50">
                    {/* @ts-expect-error - Chip component type issue */}
                    <Chip color="gray" className="!text-primary">
                        Drop here
                    </Chip>
                </div>
            )}
            {/* Header buttons for Annotate mode */}
            {isHovered &&
                (!flowLocked || textPromptMode) &&
                !textPrompts.includes(name) &&
                !isEditing && (
                    <div className="absolute right-2 top-2 z-10 flex items-center">
                        {/* @ts-expect-error - Button component type issue */}
                        <TooltipContainer
                            title="Edit object name"
                            type="titleOnly"
                            customButton={
                                /* @ts-expect-error - Button component type issue */
                                <Button
                                    onClick={handleEditClick}
                                    type="ghost"
                                    size="small"
                                    shape="circle"
                                >
                                    <i className="far fa-pencil"></i>
                                </Button>
                            }
                        />

                        <TooltipContainer
                            title={hasAnnotationsToClear ? "Clear annotations" : "Delete object"}
                            type="titleOnly"
                            customButton={
                                /* @ts-expect-error - Button component type issue */
                                <Button
                                    onClick={handleDeleteLabel}
                                    type={hasAnnotationsToClear ? "ghost" : "red-ghost"}
                                    size="small"
                                    shape="circle"
                                >
                                    <i
                                        className={
                                            hasAnnotationsToClear
                                                ? "far fa-broom-wide"
                                                : "far fa-trash-alt"
                                        }
                                    ></i>
                                </Button>
                            }
                        />
                    </div>
                )}
            <div
                className={twClassNames(
                    hasAnnotationsOnCurrentFile && "border-b border-gray-300 pb-3 pr-3"
                )}
            >
                <CardHeader
                    name={name}
                    annotationCount={textPromptMode ? undefined : realAnnotationsCount}
                    mode={BuildPromptSteps.ANNOTATE_DATA}
                    flowLocked={flowLocked}
                    isEditing={isEditing}
                    editValue={editValue}
                    inputRef={inputRef}
                    onEditClick={handleEditClick}
                    onEditKeyDown={handleEditKeyDown}
                    onEditBlur={handleEditBlur}
                    setEditValue={setEditValue}
                    labelColor={labelColor}
                    onColorChange={(newColor) =>
                        updateLabelColor(updatePromptLabels, promptLabels, name, newColor)
                    }
                />
            </div>

            {textPromptMode && !flowLocked && !hasAnnotationsOnCurrentFile ? (
                <NoResultsEmptyState labelName={name} />
            ) : (
                <>
                    {textPromptMode && !flowLocked && hasAnnotationsOnCurrentFile && (
                        <ConfidenceSlider
                            initialConfidenceThreshold={initialConfidenceThreshold}
                            onConfidenceThresholdChanged={(newThreshold) => {
                                const promptLabel = promptLabels.get(name);
                                updatePromptLabels([
                                    {
                                        name,
                                        confidenceThreshold: newThreshold,
                                        customColor: promptLabel?.customColor
                                    }
                                ]);
                            }}
                            name={name}
                            predictionCount={visibleAnnotationsForCurrentFile.length}
                            mode={BuildPromptSteps.REVIEW_PREDICTIONS}
                            className="pb-2 pt-1"
                        />
                    )}

                    {!(flowLocked && annotationsForThisLabel.length === 0) &&
                        hasAnnotationsOnCurrentFile && (
                            <CardThumbnails
                                name={name}
                                mode={
                                    textPromptMode
                                        ? BuildPromptSteps.REVIEW_PREDICTIONS
                                        : BuildPromptSteps.ANNOTATE_DATA
                                }
                                thumbnails={thumbnails}
                                selectedObject={selectedObject}
                                previouslySelectedObject={previouslySelectedObject}
                                flowLocked={flowLocked}
                                onThumbnailClick={textPromptMode ? undefined : handleThumbnailClick}
                                onDeleteAnnotationClick={handleDeleteAnnotationClick}
                                onThumbnailDragStart={handleThumbnailDragStart}
                                onThumbnailDragEnd={handleThumbnailDragEnd}
                                labelColor={labelColor}
                            />
                        )}
                </>
            )}
        </div>
    );
};
